"use client";

import Link from "next/link";
import Image from "next/image";
import { motion, Transition } from "framer-motion";
import { BlogPost } from "@/data/blogs";

const commonTransition: Transition = {
  duration: 1.2,
  ease: "easeInOut",
  repeat: Infinity,
  repeatType: "loop",
  repeatDelay: 0.8,
};

const BlogDetailsHero = ({ blog }: { blog: BlogPost }) => {
  const lastModified = blog.updatedAt
    ? new Date(blog.updatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <>
      <section className="relative overflow-hidden bg-[#0a0a1a] pt-32 pb-20 bg-[url('/blogDetailsHeroBg.webp')] bg-no-repeat bg-cover">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-blue-600/20 rounded-full blur-[120px]" />
          <div className="absolute top-20 left-1/4 w-[300px] h-[300px] bg-purple-600/15 rounded-full blur-[100px]" />
          <div className="absolute top-10 right-1/4 w-[250px] h-[250px] bg-indigo-500/15 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 text-center text-[#2B2B2B] max-w-[1300] mx-auto px-4 lg:px-0">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="flex items-center justify-center gap-2 text-xs text-gray-700 mb-6"
          >
            <Link href="/" className="hover:text-gray-800 transition">
              Home
            </Link>
            <span>/</span>
            <Link href="/blog" className="hover:text-gray-800 transition">
              Blog
            </Link>
            <span>/</span>
            <span className="text-black truncate max-w-[200px]">
              {blog.title}
            </span>
          </nav>

          <div className="section_title_border_light w-fit mx-auto text-black rounded-full mb-6">
            <div className="how_trackforce_works px-4 py-2 text-sm font-semibold">
              {blog.tag || "Blog"}
            </div>
          </div>

          <h1 className="text-2xl md:text-[42px] font-semibold leading-tight">
            {blog.hero.titleTop}
          </h1>

          <div className="relative inline-block mt-[2]">
            <h2 className="text-2xl md:text-[42px] font-semibold leading-tight">
              {blog.hero.titleBottomPrefix}
              <span className="font-playball font-normal">
                {blog.hero.titleItalic}
              </span>
            </h2>
            <div className="absolute -right-13 -top-2 rotate-120">
              <motion.svg
                width="37"
                height="37"
                viewBox="0 0 37 37"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.path
                  d="M22.3966 31.5582C22.3966 31.5582 16.7601 32.0442 11.4624 34.0293"
                  fill="none"
                  stroke="black"
                  strokeOpacity="0.64"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={commonTransition}
                />
                <motion.path
                  d="M24.4997 23.2532C17.4498 17.8954 8.26279 15.3839 2.49653 13.8603"
                  fill="none"
                  stroke="black"
                  strokeOpacity="0.64"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={commonTransition}
                />
                <motion.path
                  d="M33.6205 19.1061C33.035 14.4653 22.104 2.80171 22.104 2.80171"
                  fill="none"
                  stroke="black"
                  strokeOpacity="0.64"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={commonTransition}
                />
              </motion.svg>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap justify-center items-center gap-2 md:gap-3 font-semibold mt-4 text-sm md:text-base">
            <p>{blog.date}</p>
            <p>•</p>
            <p>Author: {blog.author}</p>
            {blog.readingTime && (
              <>
                <p>•</p>
                <p>~{blog.readingTime} min read</p>
              </>
            )}
            {lastModified && lastModified !== blog.date && (
              <>
                <p>•</p>
                <p className="font-normal text-black text-xs">
                  Updated {lastModified}
                </p>
              </>
            )}
            {/* <p>•</p> */}
            {/* <p>{blog.comments ?? "No comments yet"}</p> */}
          </div>
        </div>
      </section>

      {/* Author bio card */}
      {blog.authorBio && (
        <div className="bg-[#0d1225] border-b border-blue-900/30">
          <div className="max-w-[1300px] mx-auto px-4 lg:px-0 py-6 flex items-start gap-4">
            {blog.authorAvatar ? (
              <Image
                src={blog.authorAvatar}
                alt={blog.author}
                width={52}
                height={52}
                className="rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-13 h-13 rounded-full bg-blue-600/30 flex items-center justify-center text-blue-300 font-semibold text-lg shrink-0">
                {blog.author.charAt(0)}
              </div>
            )}
            <div>
              <p className="font-semibold text-black text-sm">{blog.author}</p>
              <p className="text-gray-800 text-sm mt-1 leading-relaxed">
                {blog.authorBio}
              </p>
            </div>
          </div>
        </div>
      )}
       {blog.image && (
        <div className="relative w-full h-[400px] md:h-[500px] lg:h-[550px] max-w-[1300px] mx-auto px-4 lg:px-0 my-5 rounded-lg overflow-hidden">
          <Image
            src={blog.image}
            alt={blog.title}
            fill
            className="object-contain object-center rounded-lg"
          />
        </div>
      )}
    </>
  );
};

export default BlogDetailsHero;
